// D center dropdown
// D as you type, center within dropdown
// D - fix race condition use onMouseDown/onTouchStart
// D fix parsing non on-hour times (e.g. "545pm")
// D select default dropdown time / what you're typing
// D should center as you type
// D styling
// D enter, escape, keydown, keyup
// D stops responding to keypress on remount
// D hover should change what is highlighted probably? i.e. remove existing highlight
// D option to disable
// N when you delete everything dropdown should disappear? and then if you start typing should reappear
// D error on invalid time
// D remove dropdown on invalid time
// D hit enter. dropdown disappears. should reappear if you delete everything
// unselect but center if as you type, option is not present
// centerTime / defaultTime should be separated. e.g. delete everything, should center but not highlight
// arrowing to the top-most element doesn't work
// make placeholder text light when it's focused
// fix styling (look like the dropdown; hover color/padding/etc.)

// How it works:
// - on focus, a dropdown opens allowing you to select a time
// - you can also type a time: it will parse the input as you type, e.g. "8am", and also focus on the right dropdown row
// - on blur, it will set the time and normalize whatever string you've typed in, e.g. "8am" will become "8:30 am"
// - if type something unparseable, dropdown disappears and field highlights red. delete / type something parseable again and

// value is a prop. it should be an integer representing seconds since midnight
// the inputString is stored in the state.

import { Component } from "react";
import ReactDOM from "react-dom";
import { Input } from "semantic-ui-react";
import styled from "styled-components";
import {
  parseTimeString,
  timeDisplay,
  defaultTimes,
} from "common/helpers/timepicker";

class Timepicker extends Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: false, inputString: "" };
  }

  // parse the input string and return a time integer
  parsedTimeInt() {
    return parseTimeString(this.state.inputString);
  }

  // normalized version of the input string. e.g. if "8am" is the input string, return "8:00 am"
  normalizedTimeString() {
    return timeDisplay(this.parsedTimeInt()) || "";
  }

  // normalize the input string. e.g. set it from "8am" to "8:00 am"
  normalizeTime(callback = null) {
    this.setState({ inputString: this.normalizedTimeString() }, callback);
  }

  // called when a time is selected from the dropdown
  // immediately set the time by closing the dropdown, setting the input string to the normalized value, and propagating the change
  selectTime(timeInt) {
    this.setState(
      {
        isOpen: false,
        inputString: timeDisplay(timeInt),
      },
      this.propagateChange
    );
  }

  // when a new time is set, return it to the parent
  // this will happen when we choose a time from the dropdown, or focus away
  propagateChange() {
    if (this.props.onChange) {
      this.props.onChange({
        display: this.normalizedTimeString(),
        value: this.parsedTimeInt(),
      });
    }
  }

  unparseableInput() {
    if (this.state.inputString && this.parsedTimeInt() == null) {
      return true;
    }
    return false;
  }

  // make sure the input box reflects the normalized time, using whatever value was passed as a prop
  // value is an integer representing seconds since midnight
  _initInput(props) {
    let inputString = null;
    if (props.value != null) {
      inputString = timeDisplay(props.value);
    } else {
      inputString = "";
    }

    this.setState({ isOpen: false, inputString });
  }

  componentWillMount() {
    this._initInput(this.props);
  }

  // if we update the value for whatever reason, display the new time
  componentWillReceiveProps(nextProps) {
    this._initInput(nextProps);
  }

  render() {
    const cantParse = this.unparseableInput();

    return (
      <div style={{ width: "100%", position: "relative" }}>
        <Input fluid={this.props.fluid} error={this.props.error || cantParse}>
          <input
            onChange={(e) =>
              this.setState({ inputString: e.target.value, isOpen: true })
            }
            onBlur={(e) => {
              this.normalizeTime(() =>
                this.setState({ isOpen: false }, this.propagateChange)
              );
            }}
            value={this.state.inputString}
            onFocus={(event) => {
              event.target.select();
              this.setState({ isOpen: true });
            }}
            onClick={(event) => {
              event.target.select();
              this.setState({ isOpen: true });
            }}
            style={{ width: "100%", ...(this.props.style || {}) }}
            onKeyDown={(e) => {
              if (this.refTimepickerList)
                this.refTimepickerList._handleKeyPress(e);
            }}
            placeholder="--"
            disabled={this.props.disabled}
            tabIndex={this.props.tabIndex}
          />
        </Input>
        {this.state.isOpen &&
          !cantParse && ( // override state and immediately close timepicker if we can't parse the input string
            <TimepickerList
              ref={(el) => (this.refTimepickerList = el)}
              selectTime={this.selectTime.bind(this)}
              defaultTime={
                !_.isNil(this.parsedTimeInt())
                  ? this.parsedTimeInt()
                  : parseTimeString(this.props.defaultTime)
              }
            />
          )}
      </div>
    );
  }
}

const TimepickerListStyled = styled.div.attrs({
  className: "ui menu vertical",
})`
  max-height: 180px;
  overflow-y: scroll;
  position: absolute;
  width: 100%;
  z-index: 5;
  margin-top: 7px !important;
  font-size: 1em;
`;

const TimepickerListItemStyled = styled.div.attrs({
  className: "item",
})`
  cursor: pointer;
  background-color: ${(props) =>
    props.selected ? "#f2f2f2 !important" : null};
`;

const timeOptions = defaultTimes();

class TimepickerList extends Component {
  constructor(props) {
    super(props);
    this.state = { highlightedTime: props.defaultTime };
    this.refList = null; // React.createRef()
    this.refDefaultListItem = null; // React.createRef()
  }

  _scrollToDefaultListItem() {
    if (this.refDefaultListItem) {
      const domRefList = ReactDOM.findDOMNode(this.refList);
      const domRefDefaultListItem = ReactDOM.findDOMNode(
        this.refDefaultListItem
      );
      domRefList.scrollTo(0, domRefDefaultListItem.offsetTop - 40);
    }
  }

  _adjustViewportForListItem() {
    if (this.refDefaultListItem) {
      const domRefList = ReactDOM.findDOMNode(this.refList);
      const domRefDefaultListItem = ReactDOM.findDOMNode(
        this.refDefaultListItem
      );

      if (domRefDefaultListItem.offsetTop < domRefList.scrollTop) {
        domRefList.scrollTo({
          top: domRefDefaultListItem.offsetTop,
          behavior: "smooth",
        });
      }

      if (
        domRefDefaultListItem.offsetTop + domRefDefaultListItem.offsetHeight >
        domRefList.scrollTop + domRefList.offsetHeight
      ) {
        domRefList.scrollTo({
          top:
            domRefDefaultListItem.offsetTop -
            domRefList.offsetHeight +
            domRefDefaultListItem.offsetHeight,
          behavior: "smooth",
        });
      }
    }
  }

  // handle keys for navigating the dropdown
  _handleKeyPress(event) {
    console.log("event.key=", event.key);
    if (event.key == "Enter") {
      this.props.selectTime(this.state.highlightedTime);
    } else if (event.key == "ArrowUp") {
      console.log("incrementing arrow up");
      this._incrementHighlightedItem(-1);
    } else if (event.key == "ArrowDown") {
      console.log("incrementing arrow down");
      this._incrementHighlightedItem(1);
    }
  }

  // change highlight the next or previous item
  _incrementHighlightedItem(direction = 1) {
    const currentIndex = _.indexOf(timeOptions, this.state.highlightedTime);
    const newIndex = currentIndex + direction;

    if (newIndex >= 0 && newIndex < timeOptions.length) {
      this.setState(
        { highlightedTime: timeOptions[newIndex] },
        this._adjustViewportForListItem
      );
    }
  }

  // component mounts when dropdown opens
  componentDidMount() {
    this._scrollToDefaultListItem();
  }

  componentWillReceiveProps(nextProps) {
    // as the default time changes, update the highlighted item. e.g. user types a time
    if (nextProps.defaultTime != this.props.defaultTime) {
      this.setState(
        { highlightedTime: nextProps.defaultTime },
        this._scrollToDefaultListItem
      );
    }
  }

  render() {
    const { defaultTime } = this.props;

    return (
      <TimepickerListStyled ref={(el) => (this.refList = el)}>
        {timeOptions.map((timeInt) => {
          const isHighlightedTime =
            !_.isNil(this.state.highlightedTime) &&
            timeInt == this.state.highlightedTime;

          return (
            <TimepickerListItemStyled
              selected={isHighlightedTime}
              ref={(el) => {
                if (isHighlightedTime) this.refDefaultListItem = el;
              }}
              onMouseDown={() => this.props.selectTime(timeInt)}
              onMouseOver={() => this.setState({ highlightedTime: timeInt })}
              onMouseLeave={() => this.setState({ highlightedTime: null })}
            >
              {timeDisplay(timeInt)}
            </TimepickerListItemStyled>
          );
        })}
      </TimepickerListStyled>
    );
  }
}

export default Timepicker;
